import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { nipClient } from 'application/nip';
import { isEmpty } from 'lodash';
import qs from 'qs';
import { ICountry, IPlan, IPlanListItem, IPlanProgress, IPlanResult, IResponseWrapper, IUser } from 'types/dto';
import { IFetchQuery } from 'types/net';
import { URLSearchParams } from 'url';
import { extractResponseData, handleCommandResponseData, handleDownloadFileResponse } from 'utils/net';
import { IFetchCountriesQuery } from './country';

export type IFetchPlanQuery =
  | IFetchQuery
  | {
      typeId?: number;
      countryId?: number;
      adminAreaId?: number;
      latest?: boolean;
    };

export type IFetchPlanProgressQuery = {
  countryId?: number;
};

export async function createPlan(data: IPlan) {
  return nipClient.post('/plans', data).then(handleCommandResponseData<{ id: number }>);
}

export async function fetchPlans(query: IFetchPlanQuery): Promise<IResponseWrapper<IPlanListItem[]>> {
  return nipClient
    .get(`/plans${!isEmpty(query) ? '?' + qs.stringify(query) : ''}`)
    .then(extractResponseData<IResponseWrapper<IPlanListItem[]>>);
}

export function usePlans(query: IFetchPlanQuery = {}, options?: UseQueryOptions<IResponseWrapper<IPlanListItem[]>>) {
  const queryString = qs.stringify(query);
  return useQuery<IResponseWrapper<IPlanListItem[]>>(['plans', queryString], () => fetchPlans(query), options);
}

export function downloadPlanFile(id: number): Promise<void> {
  return nipClient
    .get(`/plans/${id}/file`, {
      responseType: 'blob',
    })
    .then(res => handleDownloadFileResponse(res));
}
export function downloadPlanResultFile(id: number): Promise<void> {
  return nipClient
    .get(`/plans/results/${id}/file`, {
      responseType: 'blob',
    })
    .then(res => handleDownloadFileResponse(res));
}

export async function fetchCountries(query: IFetchCountriesQuery = {}): Promise<ICountry[]> {
  return nipClient
    .get('/plans/countries' + (!isEmpty(query) ? `?${qs.stringify(query)}` : ''))
    .then(handleCommandResponseData<ICountry[]>);
}

export async function deletePlan(id: number): Promise<string> {
  return nipClient.delete(`/plans/${id}`).then(handleCommandResponseData<string>);
}

export async function fetchPlanUsers(searchParams: URLSearchParams): Promise<IResponseWrapper<IUser[]>> {
  const queryString = searchParams.toString();
  return nipClient
    .get(`/plans/users${queryString ? '?' + queryString : ''}`)
    .then(extractResponseData<IResponseWrapper<IUser[]>>);
}

export function usePlanUsers(searchParams: URLSearchParams, options?: UseQueryOptions<IResponseWrapper<IUser[]>>) {
  return useQuery<IResponseWrapper<IUser[]>>(
    ['planUsers', searchParams.toString()],
    () => fetchPlanUsers(searchParams),
    options,
  );
}

export function useCountries(query: IFetchCountriesQuery, options?: UseQueryOptions<ICountry[]>) {
  const queryString: string = qs.stringify(query);
  return useQuery<ICountry[]>(['country', queryString], () => fetchCountries(query), options);
}

export async function fetchPlanProgress(query: IFetchPlanProgressQuery): Promise<IResponseWrapper<IPlanProgress[]>> {
  return nipClient
    .get('/plans/progress' + (!isEmpty(query) ? `?${qs.stringify(query)}` : ''))
    .then(extractResponseData<IResponseWrapper<IPlanProgress[]>>);
}

export function usePlanProgress(
  query: IFetchPlanProgressQuery,
  options?: UseQueryOptions<IResponseWrapper<IPlanProgress[]>>,
) {
  const queryString: string = qs.stringify(query);
  return useQuery<IResponseWrapper<IPlanProgress[]>>(
    ['planProgress', queryString],
    () => fetchPlanProgress(query),
    options,
  );
}

export async function fetchPlanResults(query: IFetchPlanProgressQuery): Promise<IResponseWrapper<IPlanResult[]>> {
  return nipClient
    .get('/plans/results' + (!isEmpty(query) ? `?${qs.stringify(query)}` : ''))
    .then(extractResponseData<IResponseWrapper<IPlanResult[]>>);
}

export function usePlanResults(
  query: IFetchPlanProgressQuery,
  options?: UseQueryOptions<IResponseWrapper<IPlanResult[]>>,
) {
  const queryString: string = qs.stringify(query);
  return useQuery<IResponseWrapper<IPlanResult[]>>(
    ['planResults', queryString],
    () => fetchPlanResults(query),
    options,
  );
}
